// src/pages/Landing.jsx
import React, { useState, useEffect, useRef } from 'react';
import ODFLogo from '../components/ODFLogo';
import LanguageSelector from '../components/LanguageSelector';
import { t } from '../lib/i18n';
import HeroCanvas from '../components/HeroCanvas';
import HeroAtmosphere from '../components/HeroAtmosphere';
import SectionDivider from '../components/SectionDivider';
import { useCountUp } from '../hooks/useAnimations';
import CommandCenter from '../components/CommandCenter';
import FeatureMockup from '../components/FeatureMockup';
import { IconMail, IconInstagram, IconAlert, IconCheck } from '../components/Icon';

const G = '#00e5a0';

// ── FEATURES ──────────────────────────────────────────────────────
const FEATURES = [
  {
    tag:'01', icon:'', title:'Trade Journal',
    badge:'Core', badgeColor: G,
    desc:'Manually log every trade in under 60 seconds. Pair, direction, entry, stop loss, take profit, session, emotions before and after, mistakes, notes, and screenshot link. Log as open or closed — close open trades later with full exit results.',
    bullets:['Log any trade in under 60 seconds','Open trades tracked live, closed later','Pre-trade checklist — rule breaks flagged automatically','Mistake tags + emotion state per trade'],
  },
  {
    tag:'02', icon:'', title:'Performance Dashboard',
    badge:'Core', badgeColor: G,
    desc:'Your complete performance picture — updated automatically with every trade. Win rate, ROI %, P&L in both $ and %, profit factor, average RR achieved, max drawdown, current drawdown, discipline score, equity curve, daily P&L chart, session breakdown, pair breakdown, weekly P&L, best/worst day, and most common mistakes.',
    bullets:['ROI % + P&L in $ and % always visible','Equity curve + daily + weekly charts','Session performance (London, NY, Asian)','Pair performance breakdown','Best and worst trading day','Most common mistake patterns'],
  },
  {
    tag:'03', icon:'', title:'ODF Score',
    badge:'Core', badgeColor: G,
    desc:'A composite performance grade from 0 to 100 across 6 weighted dimensions. Not just a number — each dimension has its own score, a progress bar, and specific actionable tips telling you exactly what to improve.',
    bullets:['Profitability · Consistency · Risk Control','Execution · Discipline · Sample Size','Grade: D → C → B → B+ → A → A+','Actionable improvement tip per weak dimension'],
  },
  {
    tag:'04', icon:'', title:'Pattern Insights',
    badge:'Core+Pro', badgeColor:'#4cc9f0',
    desc:'Automatically surfaces hidden patterns from your trade data — no manual analysis needed. Finds your best pair, worst pair, best session, the exact dollar cost of your most expensive habit, revenge trading patterns, and your optimal trade count per day.',
    bullets:['Best and worst pair by win rate','Dollar cost of each bad habit','Revenge trading cost quantified','Your trade count sweet spot per day'],
  },
  {
    tag:'05', icon:'', title:'Performance Coach',
    badge:'Pro', badgeColor:'#ffd166',
    desc:'The system reads your last 20 trades and all your stats, then writes a personalised coaching report. Not generic advice — every sentence references your actual numbers. Once per day per user.',
    bullets:['Personalised to your trade data','Performance summary, edge, weakness','3 actionable steps from your data','Mindset note from your emotion patterns'],
  },
  {
    tag:'06', icon:'', title:'Performance Lab',
    badge:'Pro', badgeColor:'#ffd166',
    desc:'Deep 7-question diagnostic on your full trade history. Identifies where you lose, whether your edge is statistically real, your most expensive habit with exact dollar cost, a fixed-risk simulation, your psychological profile, and what needs to change to reach profitability.',
    bullets:['7-question deep diagnostic','Fixed 1% risk simulation','Breakeven calculation — exact numbers','3-focus 30-day improvement plan'],
  },
  {
    tag:'07', icon:'', title:'Risk Manager & Position Calculator',
    badge:'Core', badgeColor: G,
    desc:'Pre-session command center with a full position size calculator for both Forex and Futures. Set daily risk limits and max trade count. Pre-session mental checklist. Everything you need to enforce your rules before entering a trade.',
    bullets:['Forex: pip-based position sizing','Futures: NQ, ES, MNQ, MES, YM, GC, CL','Daily loss limit + max trades enforced','Pre-session mental checklist'],
  },
  {
    tag:'08', icon:'', title:'Discipline Tracker',
    badge:'Core', badgeColor: G,
    desc:'Tracks rule adherence, emotional states, and mistake patterns across all your sessions. Identifies your most repeated mistake, your win rate by emotional state, and whether revenge trading is costing you money.',
    bullets:['Rule adherence % over time','Win rate by emotion (Calm vs Frustrated)','Most repeated mistake identification','Revenge trading pattern detection'],
  },
  {
    tag:'09', icon:'', title:'Calendar View',
    badge:'Core', badgeColor: G,
    desc:'Your complete trading history mapped day by day. Toggle between $ P&L and % return view. See trade count, win/loss dots, and P&L on every day at a glance. Click any day to expand the full trade breakdown for that session.',
    bullets:['$ and % toggle — switch any time','Trade count + win/loss per day','Click any day for full breakdown','Monthly summary stats at the top'],
  },
  {
    tag:'10', icon:'', title:'Live TradingView Charts',
    badge:'Core', badgeColor: G,
    desc:'Full TradingView Advanced Chart embedded directly in ODFTrades — not a basic viewer. Search any symbol across all markets. Use all drawing tools: trendlines, Fibonacci, channels, shapes. All timeframes from 1m to Monthly. Full indicator library. Full screen mode.',
    bullets:['Search any pair — Forex, Futures, Crypto, Stocks','Full drawing tools — Fib, trendlines, channels','All timeframes: 1m, 5m, 15m, 1H, 4H, Daily, Weekly','All indicators: MA, RSI, MACD, Bollinger + more'],
  },
  {
    tag:'11', icon:'', title:'Economic Calendar',
    badge:'Core', badgeColor: G,
    desc:'TradingView economic calendar embedded directly in the Markets section. See all high, medium, and low impact events. Filter by country — USD, EUR, GBP, AUD, JPY, CAD, CHF, CNY. Know exactly what events could move the market before your session.',
    bullets:['High, medium, low impact events','Filter by currency and country','Updated in real time','Stays inside ODFTrades — no tab switching'],
  },
  {
    tag:'12', icon:'', title:'CSV Import / Export',
    badge:'Core', badgeColor: G,
    desc:'Import your full trade history from MetaTrader 4, MT5, TradingView, NinjaTrader, Tradovate, Interactive Brokers, or any CSV format. Custom column mapper for unsupported brokers. Export all 22 fields of your data anytime.',
    bullets:['MT4 and MT5 auto-detected','Custom column mapper for any broker','Duplicate detection on import','Export: date, pair, P&L, RR, emotions, 22 fields'],
  },
  {
    tag:'13', icon:'', title:'Shareable Trade Data Analytics',
    badge:'Core', badgeColor: G,
    desc:'Every account gets a unique public analytics link — your Trade Data Analytics Database. Share with a prop firm, mentor, or audience. Shows win rate, ROI %, profit factor, discipline score, equity curve, monthly returns, and pair performance. No balance shown publicly.',
    bullets:['Unique analytics link per account','Credibility tier badge (Unverified → Elite)','ROI % and monthly returns visible','No balance shown — performance % only'],
  },
  {
    tag:'14', icon:'', title:'Multiple Accounts',
    badge:'Core', badgeColor: G,
    desc:'Track your live broker account, prop firm challenge, and practice account separately — each with its own full dashboard, equity curve, stats, and performance history. Switch between accounts from the top bar in one click.',
    bullets:['Separate dashboard per account','Live, Prop Firm, Practice account types','Each account has its own equity curve','Switch in one click from top bar'],
  },
];

// ── FAQS ──────────────────────────────────────────────────────────
const FAQS = [
  { q:'What is Performance Lab?', a:'Performance Lab is the main trading performance product built by ODFTrades. It helps traders journal trades, import or record trading activity, review performance, identify behavioural patterns, track mistakes, measure execution, and turn raw trading data into clearer rules for improvement. It covers the full workflow: pre-session analysis with live charts and economic calendar, real-time position sizing, trade logging, data-driven coaching, deep diagnostics, and post-session review.' },
  { q:'How is it different from a spreadsheet or Notion journal?', a:'A spreadsheet gives you raw data. Performance Lab gives you a complete performance infrastructure — automatic equity curves, win rate tracking, ODF Score grading, performance coaching, pattern detection, discipline tracking, and shareable dashboard analytics. It finds patterns in your data that you would never spot manually.' },
  { q:'What is the ODF Score?', a:'The ODF Score is a composite grade (0–100) across 6 weighted dimensions: Profitability (25%), Consistency (20%), Risk Control (20%), Execution (15%), Discipline (15%), and Sample Size (5%). It grades you from D to A+ and gives you specific actionable tips for your weakest dimension.' },
  { q:'What does the Performance Coach do?', a:'The Performance Coach analyses your last 20 trades plus your overall stats and generates a personalised coaching report. It covers your performance summary, what is working, your biggest weakness with specific dollar amounts, and 3 actionable steps based on your actual data. Not generic advice — it references your real numbers.' },
  { q:'What is the Performance Lab?', a:'The Performance Lab runs a 7-question diagnostic on your trade history: Where are you losing? Do you have a real edge? What is your most expensive habit? Are you a good trader with bad risk management? What would fixed 1% risk look like? What does your psychological profile show? What needs to change to reach profitability? It then generates a 3-focus improvement plan.' },
  { q:'What markets does ODFTrades support?', a:'All major and minor forex pairs, gold, silver, NAS100, US30, SPX500, crude oil, major cryptocurrencies, and futures including NQ, ES, MNQ, MES, YM, MYM, GC, CL. The position size calculator handles both forex pip values and futures contract sizes.' },
  { q:"Can I log open trades I haven't closed yet?", a:'Yes. Log your entry as an open trade, then close it later from the Journal with your exit price, P&L, and result. Open trades are tracked separately and excluded from your closed-trade stats until you close them.' },
  { q:'Can I import my MetaTrader history?', a:'Yes. Export from MT4 or MT5 and upload the CSV. ODFTrades auto-detects the format and maps all fields automatically. For unsupported brokers, use the custom column mapper to match your CSV headers to ODFTrades fields.' },
  { q:'Is my data private?', a:'Everything is private by default. Your balance is never shown publicly. You choose what is visible on your shareable performance link. You can delete your account and all data anytime.' },
  { q:'Is there a free version?', a:'Yes. Performance Lab is free for all core features: trade journal, full dashboard, ODF Score, risk manager, live charts, calendar, CSV import/export, and shareable dashboard analytics. Pro features include the Performance Coach, full Performance Diagnostic, and Improvement Plan.' },
  { q:'What is the difference between Free and Pro?', a:'Free gives you the full trading system — journal, dashboard, ODF Score, risk tools, charts, calendar, and trade data analytics. Pro adds advanced coaching: the Performance Coach, 7-question diagnostic report, and personalised improvement plan.' },
  { q:'Does ODFTrades work on mobile?', a:'Yes. Fully responsive on all devices. Log trades, check your dashboard, and review your performance from desktop, tablet, or phone.' },
];

function useInView(threshold=0.1) {
  const ref   = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if(e.isIntersecting) setInView(true); }, { threshold });
    if(ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Reveal({ children, delay=0, style={} }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{ opacity:inView?1:0, transform:inView?'translateY(0)':'translateY(28px)', transition:`opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`, ...style }}>
      {children}
    </div>
  );
}

// Animated number — counts from 0 to target on viewport entry.
// `display` is what gets rendered; if a non-numeric value is supplied
// it's shown as-is (no animation).
function StatNumber({ value }) {
  const numeric = typeof value === 'number' ? value : (parseFloat(value) || null);
  const [ref, current] = useCountUp(numeric ?? 0, { duration: 1100 });
  if (numeric === null) {
    return <span>{value}</span>;
  }
  // Preserve the way the original string looked (e.g. integer vs decimal)
  const decimals = (String(value).split('.')[1] || '').length;
  const shown = decimals > 0 ? current.toFixed(decimals) : Math.round(current).toString();
  return <span ref={ref} style={{ fontVariantNumeric: 'tabular-nums' }}>{shown}</span>;
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom:'1px solid #1a1a26' }}>
      <button onClick={() => setOpen(o=>!o)} style={{ width:'100%', textAlign:'left', background:'none', border:'none', padding:'20px 0', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', gap:20, outline:'none' }}>
        <span style={{ fontSize:15, fontWeight:500, color:'#f0f0f5', fontFamily:"'IBM Plex Sans',sans-serif", lineHeight:1.5 }}>{q}</span>
        <span style={{ width:26, height:26, borderRadius:'50%', background:open?'rgba(0,229,160,0.15)':'rgba(255,255,255,0.05)', border:`1px solid ${open?'rgba(0,229,160,0.3)':'#2a2a3e'}`, display:'flex', alignItems:'center', justifyContent:'center', color:open?G:'#8888a8', fontSize:18, flexShrink:0, transform:open?'rotate(45deg)':'rotate(0)', transition:'all 0.25s ease', lineHeight:1 }}>+</span>
      </button>
      <div style={{ maxHeight:open?400:0, overflow:'hidden', transition:'max-height 0.35s cubic-bezier(0.16,1,0.3,1)' }}>
        <p style={{ fontSize:14, color:'#8888a8', lineHeight:1.85, paddingBottom:20, margin:0 }}>{a}</p>
      </div>
    </div>
  );
}

export default function Landing({ onEnter, onSignIn, onTrackRecord, onContact, onPrivacy, onTerms, onDisclaimer, lang='en', setLang, loggedIn=false, onDashboard }) {
  const [scrolled, setScrolled] = useState(false);
  const [activeFeature, setActiveFeature] = useState(null);
  const [pdfEmail,   setPdfEmail]   = useState('');
  const [pdfStatus,  setPdfStatus]  = useState(''); // '' | 'sending' | 'success' | 'error'
  const heroRef = useRef(null);
  const isRTL = lang === 'ar';

  // Magnetic feature-card: 3D tilt toward cursor + glow that pools at cursor
  function handleMagneticMove(e) {
    const card = e.currentTarget;
    const r = card.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const cx = r.width  / 2;
    const cy = r.height / 2;
    // Max ±6deg on Y axis, ±5deg on X
    const ry = ((x - cx) / cx) * 6;
    const rx = -((y - cy) / cy) * 5;
    card.style.setProperty('--mx', `${x}px`);
    card.style.setProperty('--my', `${y}px`);
    card.style.setProperty('--rx', `${rx.toFixed(2)}deg`);
    card.style.setProperty('--ry', `${ry.toFixed(2)}deg`);
  }
  function handleMagneticLeave(e) {
    const card = e.currentTarget;
    card.style.setProperty('--rx', '0deg');
    card.style.setProperty('--ry', '0deg');
  }

  // Magnetic CTA — softer, just glow tracks cursor
  function handleCtaMove(e) {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  }

  const handlePdfDownload = async (e) => {
    e.preventDefault();
    if (!pdfEmail || !pdfEmail.includes('@')) {
      setPdfStatus('error');
      return;
    }
    setPdfStatus('sending');
    try {
      // Capture email to Formspree (same inbox as contact form)
      await fetch('https://formspree.io/f/mvzlawvw', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: pdfEmail,
          source: 'ODF Edge PDF Download',
          subject: 'New PDF Download — ODF Edge',
        }),
      });
      // Trigger download
      const link = document.createElement('a');
      link.href = '/ODF-Edge.pdf';
      link.download = 'ODF-Edge.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setPdfStatus('success');
    } catch (err) {
      // Even if Formspree fails, still let them download
      const link = document.createElement('a');
      link.href = '/ODF-Edge.pdf';
      link.download = 'ODF-Edge.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setPdfStatus('success');
    }
  };

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn, { passive:true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <div style={{ background:'#0a0a0f', color:'#f0f0f5', fontFamily:"'IBM Plex Sans',sans-serif", overflowX:'hidden', direction:isRTL?'rtl':'ltr' }}>

      {/* ── NAV ── */}
      <nav className="landing-nav" style={{ position:'fixed', top:0, left:0, right:0, zIndex:200, background:scrolled?'rgba(10,10,15,0.96)':'transparent', backdropFilter:scrolled?'blur(16px)':'none', borderBottom:scrolled?'1px solid #1a1a26':'1px solid transparent', transition:'all 0.3s ease', padding:'0 32px', height:60, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:32 }}>
          <ODFLogo size="compact" />
          <div className="landing-nav-links" style={{ display:'flex', gap:24 }}>
            {[['features', t(lang,'features')],['about', t(lang,'about')],['faq', t(lang,'faq')]].map(([id,label]) => (
              <button key={id} type="button"
                onClick={() => {
                  const el = document.getElementById(id);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                style={{ fontSize:13, color:'#8888a8', background:'none', border:'none', cursor:'pointer', textDecoration:'none', transition:'color 0.2s', fontWeight:400, fontFamily:"'IBM Plex Sans',sans-serif", padding:0 }}
                onMouseEnter={e=>e.target.style.color='#f0f0f5'} onMouseLeave={e=>e.target.style.color='#8888a8'}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <LanguageSelector lang={lang} setLang={setLang} />
          <button onClick={onContact} style={{ fontSize:13, color:'#55556a', background:'none', border:'none', cursor:'pointer', padding:'0 8px', fontFamily:"'IBM Plex Sans',sans-serif", transition:'color 0.2s' }}
            onMouseEnter={e=>e.target.style.color='#f0f0f5'} onMouseLeave={e=>e.target.style.color='#55556a'}>
            {t(lang,'contact')}
          </button>
          {loggedIn ? (
            <button onClick={onDashboard} style={{ background:G, color:'#0a0a0f', border:'none', borderRadius:8, padding:'9px 22px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s' }}
              onMouseEnter={e=>{e.currentTarget.style.background='#00c98a';e.currentTarget.style.transform='translateY(-1px)';}}
              onMouseLeave={e=>{e.currentTarget.style.background=G;e.currentTarget.style.transform='translateY(0)';}}>
              → Dashboard
            </button>
          ) : (
            <button onClick={onSignIn} style={{ background:G, color:'#0a0a0f', border:'none', borderRadius:8, padding:'9px 22px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s' }}
              onMouseEnter={e=>{e.currentTarget.style.background='#00c98a';e.currentTarget.style.transform='translateY(-1px)';}}
              onMouseLeave={e=>{e.currentTarget.style.background=G;e.currentTarget.style.transform='translateY(0)';}}>
              {t(lang,'signIn')}
            </button>
          )}
        </div>
      </nav>

      {/* ── HERO ── */}
      <section id="hero" ref={heroRef} className="landing-hero" style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', padding:'110px 24px 80px', position:'relative', overflow:'hidden' }}>
        {/* Ambient trading canvas — candles, equity ghosts, floating R/$ tickers */}
        <HeroCanvas />
        {/* Quiet atmosphere — aurora drift, horizon lines, vignette */}
        <HeroAtmosphere />

        <Reveal style={{ position:'relative', zIndex:2 }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(0,229,160,0.08)', border:'1px solid rgba(0,229,160,0.2)', borderRadius:100, padding:'6px 16px', marginBottom:28 }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:G, animation:'pulse 2s infinite' }} />
            <span style={{ fontSize:12, color:G, fontFamily:"'DM Mono',monospace", letterSpacing:'0.08em', textTransform:'uppercase' }}>
              Trading Performance System
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.05} style={{ position:'relative', zIndex:2 }}>
          <h1 style={{ fontSize:'clamp(32px, 5.5vw, 72px)', fontWeight:700, letterSpacing:'-0.04em', lineHeight:1.08, margin:'0 0 16px', maxWidth:860 }}>
            {['Trade Smarter.', 'Analyze Deeper.', 'Build Discipline.'].map((phrase, i) => (
              <span
                key={i}
                style={{
                  display:'inline-block',
                  opacity:0,
                  transform:'translateY(22px)',
                  animation:`hero-phrase 0.9s cubic-bezier(0.16,1,0.3,1) ${0.25 + i*0.18}s forwards`,
                  marginRight: i < 2 ? 14 : 0,
                }}
              >
                {phrase}
              </span>
            ))}
          </h1>
        </Reveal>

        <Reveal delay={0.1} style={{ position:'relative', zIndex:2 }}>
          <p style={{ fontSize:15, color:'#8888a8', maxWidth:620, margin:'0 auto 20px', lineHeight:1.85 }}>
            Performance Lab is the main trading performance product built by ODFTrades — for traders who
            want to journal trades, review execution, identify behavioural patterns, and turn raw trading
            data into clearer rules for improvement.
          </p>
        </Reveal>

        <Reveal delay={0.12} style={{ position:'relative', zIndex:2 }}>
          <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap', marginBottom:36 }}>
            {['Trade Journal','Performance Coach','ODF Score','Performance Lab','Risk Manager','Live Charts','CSV Import'].map(f => (
              <span key={f} style={{ display:'flex', alignItems:'center', gap:5, background:'rgba(255,255,255,0.03)', border:'1px solid #1e1e2e', borderRadius:20, padding:'5px 13px', fontSize:12, color:'#8888a8' }}>
                <span style={{ color:G, fontSize:10 }}>✓</span> {f}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} style={{ position:'relative', zIndex:2 }}>
          {/* Hero CTAs intentionally moved to bottom of page — first glance shows positioning, not buttons */}
        </Reveal>

        {/* Product visual mockup — full command center */}
        <Reveal delay={0.18} style={{ position:'relative', zIndex:2, marginTop:48, width:'100%', maxWidth:980 }}>
          <CommandCenter />
        </Reveal>

        {/* Stats bar */}
        <Reveal delay={0.2} style={{ position:'relative', zIndex:2, marginTop:40 }}>
          <div className="landing-stats-bar" style={{ display:'flex', gap:0, background:'rgba(255,255,255,0.02)', border:'1px solid #1e1e2e', borderRadius:14, overflow:'hidden', flexWrap:'wrap' }}>
            {[
              { val:'14', label:'Features' },
              { val:'6', label:'Score Dimensions' },
              { val:'7', label:'Diagnostic Questions' },
              { val:'Free', label:'Core Platform' },
            ].map((s, i) => (
              <div key={i} style={{ padding:'18px 32px', textAlign:'center', borderRight: i < 3 ? '1px solid #1e1e2e' : 'none' }}>
                <div style={{ fontSize:22, fontWeight:700, color:G, fontFamily:"'DM Mono',monospace", marginBottom:4 }}>
                  <StatNumber value={s.val} />
                </div>
                <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <SectionDivider variant="curve" />

      {/* ── PROBLEM ── */}
      <section id="problem" style={{ padding:'100px 24px', maxWidth:900, margin:'0 auto' }}>
        <Reveal style={{ textAlign:'center', marginBottom:56 }}>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:16 }}>The problem</div>
          <h2 style={{ fontSize:'clamp(28px, 4vw, 48px)', fontWeight:700, letterSpacing:'-0.03em', margin:'0 0 16px', lineHeight:1.15 }}>Most traders lose<br />for the same reasons.</h2>
          <p style={{ fontSize:15, color:'#8888a8', maxWidth:520, margin:'0 auto', lineHeight:1.8 }}>Without a system to track, analyse, and correct your behaviour — you repeat the same mistakes indefinitely.</p>
        </Reveal>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:14 }}>
          {[
            { icon:'', title:'No performance data', desc:'You trade without knowing your actual win rate, RR, or which instruments you actually profit from.' },
            { icon:'', title:'Emotional execution', desc:'Revenge trading, FOMO entries, and oversized positions are invisible in a spreadsheet.' },
            { icon:'', title:'Repeated mistakes', desc:'The same errors keep happening because there is no system to surface, track, and eliminate them.' },
            { icon:'', title:'No accountability', desc:'Without structured tracking, you cannot prove your edge to yourself — or to anyone else.' },
          ].map((item, i) => (
            <Reveal key={i} delay={i*0.07}>
              <div style={{ background:'linear-gradient(135deg, rgba(255,77,109,0.06) 0%, #0d0d14 100%)', border:'1px solid rgba(255,77,109,0.2)', borderRadius:12, padding:'22px 20px', height:'100%', boxSizing:'border-box', position:'relative', overflow:'hidden', boxShadow:'0 0 30px rgba(255,77,109,0.05)' }}>
                <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg, rgba(255,77,109,0.5), rgba(255,77,109,0.1))' }} />
                {item.icon && <div style={{ fontSize:22, marginBottom:12 }}>{item.icon}</div>}
                <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:8 }}>{item.title}</div>
                <div style={{ fontSize:12, color:'#8888a8', lineHeight:1.7 }}>{item.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <SectionDivider variant="line" />

      {/* ── HOW IT WORKS ── */}
      <section id="how" style={{ padding:'100px 24px', maxWidth:960, margin:'0 auto' }}>
        <Reveal style={{ textAlign:'center', marginBottom:56 }}>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:16 }}>How it works</div>
          <h2 style={{ fontSize:'clamp(28px, 4vw, 46px)', fontWeight:700, letterSpacing:'-0.03em', margin:'0 0 16px', lineHeight:1.15 }}>Your full trading workflow.<br />One platform.</h2>
          <p style={{ fontSize:15, color:'#8888a8', maxWidth:540, margin:'0 auto', lineHeight:1.8 }}>ODFTrades is designed around how Traders actually work — before, during, and after every trade.</p>
        </Reveal>
        <div className="how-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:14 }}>
          {[
            { phase:'Pre-Session', color:'#4cc9f0', icon:'', steps:['Check economic calendar for high-impact events','Review TradingView charts — search any pair','Set daily risk limit and max trade count','Complete pre-session mental checklist'] },
            { phase:'In Session', color:G, icon:'', steps:['Use position size calculator (Forex + Futures)','Log the trade — open or closed status','Pre-trade checklist flags rule breaks automatically','Close open trades with exit price and results'] },
            { phase:'Post-Session', color:'#ffd166', icon:'', steps:['Review ODF Score and 6 dimension breakdown','Check Performance Coach report for personalised insights','Run Performance Lab diagnostic when needed','Track improvement plan progress over time'] },
          ].map((phase, i) => (
            <Reveal key={i} delay={i*0.1}>
              <div style={{ background:'#0d0d14', border:`1px solid ${phase.color}20`, borderRadius:14, padding:'26px 22px', height:'100%', boxSizing:'border-box' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:18 }}>
                  <span style={{ fontSize:20 }}>{phase.icon}</span>
                  <div>
                    <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:2 }}>Phase {i+1}</div>
                    <div style={{ fontSize:14, fontWeight:600, color:phase.color }}>{phase.phase}</div>
                  </div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:9 }}>
                  {phase.steps.map((step, j) => (
                    <div key={j} style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                      <span style={{ color:phase.color, fontSize:11, marginTop:2, flexShrink:0 }}>→</span>
                      <span style={{ fontSize:12, color:'#8888a8', lineHeight:1.65 }}>{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" style={{ padding:'100px 24px', maxWidth:1100, margin:'0 auto' }}>
        <Reveal style={{ textAlign:'center', marginBottom:56 }}>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:16 }}>Everything included</div>
          <h2 style={{ fontSize:'clamp(28px, 4vw, 46px)', fontWeight:700, letterSpacing:'-0.03em', margin:'0 0 16px', lineHeight:1.15 }}>12 features built for<br />Traders.</h2>
          <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap', marginTop:20 }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#8888a8' }}><div style={{ width:8, height:8, borderRadius:2, background:G }} /> Core — Free</div>
            <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#8888a8' }}><div style={{ width:8, height:8, borderRadius:2, background:'#ffd166' }} /> Pro</div>
            <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#8888a8' }}><div style={{ width:8, height:8, borderRadius:2, background:'#4cc9f0' }} /> Core + Pro upgrade</div>
          </div>
        </Reveal>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:14 }}>
          {FEATURES.map((f, i) => (
            <Reveal key={i} delay={i*0.04}>
              <div
                className="card-magnetic"
                onClick={() => setActiveFeature(activeFeature === i ? null : i)}
                onMouseMove={handleMagneticMove}
                onMouseLeave={handleMagneticLeave}
                style={{ background:activeFeature===i?'#111118':'#0d0d14', border:`1px solid ${activeFeature===i?f.badgeColor+'55':'#1a1a26'}`, borderRadius:14, padding:'18px 20px 22px', cursor:'pointer', height:'100%', boxSizing:'border-box', position:'relative', overflow:'hidden' }}
              >
                {/* Mini UI preview */}
                <FeatureMockup index={i} />

                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    {f.icon && <span style={{ fontSize:20 }}>{f.icon}</span>}
                    <div>
                      <div style={{ fontSize:10, color:'#2a2a3e', fontFamily:"'DM Mono',monospace", marginBottom:2 }}>{f.tag}</div>
                      <div style={{ fontSize:14, fontWeight:600, color:'#f0f0f5' }}>{f.title}</div>
                    </div>
                  </div>
                  <div style={{ fontSize:9, color:f.badgeColor, background:`${f.badgeColor}12`, border:`1px solid ${f.badgeColor}25`, borderRadius:5, padding:'2px 8px', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.06em', flexShrink:0 }}>
                    {f.badge}
                  </div>
                </div>
                <div style={{ fontSize:12, color:'#8888a8', lineHeight:1.7, marginBottom: activeFeature===i ? 14 : 0, position:'relative', zIndex:1 }}>{f.desc}</div>
                {activeFeature === i && (
                  <div style={{ display:'flex', flexDirection:'column', gap:5, borderTop:'1px solid #1e1e2e', paddingTop:12, position:'relative', zIndex:1 }}>
                    {f.bullets.map((b, bi) => (
                      <div key={bi} style={{ display:'flex', gap:7, alignItems:'flex-start', fontSize:11, color:'#8888a8' }}>
                        <span style={{ color:f.badgeColor, flexShrink:0, marginTop:1 }}>✓</span> {b}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── PERFORMANCE COACH SPOTLIGHT ── */}
      <section id="ai" style={{ padding:'100px 24px', maxWidth:900, margin:'0 auto' }}>
        <Reveal style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.12em' }}>Performance Lab</div>
        </Reveal>
        <div style={{ background:'linear-gradient(135deg, rgba(0,229,160,0.06) 0%, rgba(76,201,240,0.04) 100%)', border:'1px solid rgba(0,229,160,0.15)', borderRadius:20, padding:'52px 48px', textAlign:'center', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', top:0, right:0, width:300, height:300, background:'radial-gradient(circle, rgba(0,229,160,0.08) 0%, transparent 70%)', pointerEvents:'none' }} />
          <Reveal>
            <div style={{ fontSize:11, color:G, fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:14 }}>Performance Coach · Pro</div>
            <h2 style={{ fontSize:'clamp(24px, 3.5vw, 42px)', fontWeight:700, letterSpacing:'-0.03em', margin:'0 0 16px', lineHeight:1.2 }}>Your personal trading coach,<br />data-driven.</h2>
            <p style={{ fontSize:15, color:'#8888a8', maxWidth:560, margin:'0 auto 28px', lineHeight:1.8 }}>
              Not generic advice. The system analyses your actual trade data — your pairs, your sessions, your mistakes, your patterns — and writes a personalised performance report specific to you.
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:12, marginBottom:32, textAlign:'left' }}>
              {[
                { iconKey:'summary',  iconColor:G,         label:'Performance Summary', desc:'Honest overview of your current results' },
                { iconKey:'edge',     iconColor:G,         label:'Your Edge', desc:'What is working — with specific numbers' },
                { iconKey:'alert',    iconColor:'#ff4d6d', label:'Biggest Weakness', desc:'The one thing costing you the most money' },
                { iconKey:'check',    iconColor:G,         label:'3 Actionable Steps', desc:'Specific changes based on your data' },
              ].map((item, i) => (
                <div key={i} style={{ background:'rgba(0,0,0,0.3)', border:'1px solid rgba(0,229,160,0.12)', borderRadius:10, padding:'14px 16px', position:'relative', overflow:'hidden' }}>
                  <div style={{ position:'absolute', top:0, left:14, right:14, height:1, background:`linear-gradient(90deg, transparent, ${item.iconColor}88, transparent)`, opacity:0.6 }} />
                  <div style={{ marginBottom:8, color:item.iconColor, display:'inline-flex', alignItems:'center', justifyContent:'center', width:26, height:26, borderRadius:6, background:`${item.iconColor}14`, border:`1px solid ${item.iconColor}33` }}>
                    {item.iconKey === 'alert' ? <IconAlert size={14} />
                      : item.iconKey === 'check' ? <IconCheck size={14} />
                      : item.iconKey === 'edge' ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/>
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="12" width="4" height="8" rx="1"/><rect x="10" y="6" width="4" height="14" rx="1"/><rect x="17" y="9" width="4" height="11" rx="1"/>
                        </svg>
                      )
                    }
                  </div>
                  <div style={{ fontSize:12, fontWeight:600, color:'#f0f0f5', marginBottom:3 }}>{item.label}</div>
                  <div style={{ fontSize:11, color:'#55556a', lineHeight:1.5 }}>{item.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>
              Analysis based on your trade data · Personalised to your results · Updates daily
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── WHO IS IT FOR ── */}
      <section id="who" style={{ padding:'100px 24px', maxWidth:960, margin:'0 auto' }}>
        <Reveal style={{ textAlign:'center', marginBottom:48 }}>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:16 }}>Who is it for</div>
          <h2 style={{ fontSize:'clamp(26px, 3.5vw, 44px)', fontWeight:700, letterSpacing:'-0.03em', margin:0 }}>Built for Traders.</h2>
        </Reveal>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:12 }}>
          {[
            { icon:'', type:'Forex Traders', desc:'Track all major, minor, and exotic pairs. Session analysis for London, NY, and Asian.' },
            { icon:'', type:'Futures Traders', desc:'NQ, ES, MNQ, MES, YM, GC and more. Contracts-based position sizing built in.' },
            { icon:'', type:'Prop Firm Traders', desc:'Separate account for your challenge. Track profit target and daily drawdown.' },
            { icon:'', type:'Part-Time Traders', desc:'Log in under 60 seconds. Review stats quickly. No complex setup.' },
            { icon:'', type:'Learning Traders', desc:'Build your edge through data. Understand what setups work and which habits cost.' },
            { icon:'', type:'Public Traders', desc:'Build a verifiable trade data analytics. Share your real performance with anyone.' },
          ].map((item, i) => (
            <Reveal key={i} delay={i*0.06}>
              <div style={{ background:'#0d0d14', border:'1px solid #1a1a26', borderRadius:12, padding:'22px 20px', transition:'border-color 0.2s', cursor:'default' }}
                onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(0,229,160,0.2)'}
                onMouseLeave={e=>e.currentTarget.style.borderColor='#1a1a26'}>
                {item.icon && <div style={{ fontSize:24, marginBottom:10 }}>{item.icon}</div>}
                <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:6 }}>{item.type}</div>
                <div style={{ fontSize:12, color:'#55556a', lineHeight:1.65 }}>{item.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── PRIVACY & SECURITY ── */}
      <section id="privacy" style={{ padding:'100px 24px', maxWidth:760, margin:'0 auto' }}>
        <Reveal style={{ textAlign:'center', marginBottom:40 }}>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:16 }}>Privacy & Security</div>
          <h2 style={{ fontSize:'clamp(26px, 3.5vw, 44px)', fontWeight:700, letterSpacing:'-0.03em', margin:'0 0 16px', lineHeight:1.15 }}>Your data. Your terms.</h2>
          <p style={{ fontSize:15, color:'#8888a8', maxWidth:520, margin:'0 auto', lineHeight:1.8 }}>
            Everything is private by default. You decide what performance data is visible to others and what stays completely private.
          </p>
        </Reveal>
        <Reveal delay={0.05}>
          <div style={{ background:'#0d0d14', border:'1px solid #1a1a26', borderRadius:14, padding:'28px 32px', display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:14 }}>
            {[
              'Private by default',
              'You control visibility',
              'Share only what you choose',
              'No third-party data access',
              'Secure authentication',
            ].map((item, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, color:'#8888a8' }}>
                <div style={{ width:18, height:18, borderRadius:'50%', background:'rgba(0,229,160,0.1)', border:'1px solid rgba(0,229,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <span style={{ fontSize:10, color:G, fontWeight:700 }}>✓</span>
                </div>
                {item}
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <SectionDivider variant="curve" />

      {/* ── FREE vs PRO ── */}
      <section id="pricing" style={{ padding:'100px 24px', maxWidth:860, margin:'0 auto' }}>
        <Reveal style={{ textAlign:'center', marginBottom:48 }}>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:16 }}>Plans</div>
          <h2 style={{ fontSize:'clamp(26px, 3.5vw, 44px)', fontWeight:700, letterSpacing:'-0.03em', margin:0 }}>Start free. Upgrade when ready.</h2>
        </Reveal>
        <div className="pricing-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          {/* Free */}
          <div style={{ background:'#0d0d14', border:'1px solid #1e1e2e', borderRadius:16, padding:'32px 28px' }}>
            <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>Free Plan</div>
            <div style={{ fontSize:32, fontWeight:700, color:'#f0f0f5', fontFamily:"'DM Mono',monospace", marginBottom:4 }}>$0</div>
            <div style={{ fontSize:13, color:'#55556a', marginBottom:24 }}>No credit card required</div>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:28 }}>
              {['Trade Journal','Full Dashboard + Equity Curve','ODF Score (full breakdown)','2 Pattern Insights','Risk Manager + Position Calculator','Forex + Futures Position Sizing','Live TradingView Advanced Charts','Economic Calendar','Calendar View ($ and %)','Discipline Tracker','CSV Import (MT4, MT5, any broker)','CSV Export (22 fields)','Shareable Trade Data Analytics','Multiple Accounts'].map(f=>(
                <div key={f} style={{ display:'flex', gap:8, alignItems:'center', fontSize:13, color:'#8888a8' }}>
                  <span style={{ color:G }}>✓</span> {f}
                </div>
              ))}
            </div>
            <button className="cta-magnetic" onClick={onEnter} onMouseMove={handleCtaMove} style={{ width:'100%', background:G, color:'#0a0a0f', border:'none', borderRadius:9, padding:'13px', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>
              Start Free →
            </button>
          </div>
          {/* Pro */}
          <div style={{ background:'linear-gradient(135deg, #111118 0%, #0d0d14 100%)', border:'1px solid rgba(255,209,102,0.25)', borderRadius:16, padding:'32px 28px', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg, #ffd166, #ffd16688)' }} />
            <div style={{ fontSize:11, color:'#ffd166', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>Pro</div>
            <div style={{ fontSize:32, fontWeight:700, color:'#f0f0f5', fontFamily:"'DM Mono',monospace", marginBottom:4 }}>Coming Soon</div>
            <div style={{ fontSize:13, color:'#55556a', marginBottom:24 }}>Early access via email</div>
            <div style={{ fontSize:11, color:'#ffd166', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>Everything in Free, plus:</div>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:28 }}>
              {['Performance Coach (written report)','7-Question Diagnostic Report','Personalised 30-Day Improvement Plan','All Pattern Insights (unlimited)','Priority support'].map(f=>(
                <div key={f} style={{ display:'flex', gap:8, alignItems:'center', fontSize:13, color:'#f0f0f5' }}>
                  <span style={{ color:'#ffd166' }}>✓</span> {f}
                </div>
              ))}
            </div>
            <button onClick={onContact} style={{ width:'100%', background:'rgba(255,209,102,0.1)', color:'#ffd166', border:'1px solid rgba(255,209,102,0.3)', borderRadius:9, padding:'13px', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s' }}
              onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,209,102,0.15)';}}
              onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,209,102,0.1)';}}>
              Get Early Pro Access →
            </button>
          </div>
        </div>
      </section>

      {/* ── TRACK RECORD ── */}
      <section id="trackrecord" style={{ padding:'100px 24px', maxWidth:800, margin:'0 auto', textAlign:'center' }}>
        <Reveal>
          {/* ODF circle */}
          <div style={{ width:72, height:72, borderRadius:'50%', background:'rgba(0,229,160,0.08)', border:'2px solid rgba(0,229,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 18px' }}>
            <span style={{ fontSize:18, fontWeight:700, color:G, fontFamily:"'DM Mono',monospace", letterSpacing:'0.04em' }}>ODF</span>
          </div>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:16 }}>ODF — Founder</div>
          <h2 style={{ fontSize:'clamp(26px, 3.5vw, 42px)', fontWeight:700, letterSpacing:'-0.03em', margin:'0 0 16px', lineHeight:1.2 }}>Built by a trader.<br />Verified by results.</h2>
          <p style={{ fontSize:15, color:'#8888a8', lineHeight:1.85, marginBottom:12, maxWidth:580, margin:'0 auto 12px' }}>
            ODF, also known as Olanrewaju Daniel Fajoyomi, has been trading since 2019 across Forex, Futures, Crypto and Stocks. ODFTrades was built out of
            necessity — not as a product idea. The trade data analytics below is live, updated with every trade logged.
          </p>
          <p style={{ fontSize:13, color:'#55556a', lineHeight:1.7, maxWidth:500, margin:'0 auto 28px' }}>
            Every user on ODFTrades gets their own shareable performance link — build your trade data analytics the same way.
          </p>
          <button onClick={onTrackRecord} style={{ background:G, color:'#0a0a0f', border:'none', borderRadius:10, padding:'13px 32px', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s' }}
            onMouseEnter={e=>{e.currentTarget.style.background='#00c98a';e.currentTarget.style.transform='translateY(-1px)';}}
            onMouseLeave={e=>{e.currentTarget.style.background=G;e.currentTarget.style.transform='translateY(0)';}}>
            ODF's Live Dashboard →
          </button>
        </Reveal>
      </section>

      {/* ── ABOUT (Performance Lab only — not the broader ODFTrades brand) ── */}
      <section id="about" style={{ padding:'100px 24px', maxWidth:760, margin:'0 auto', textAlign:'center' }}>
        <Reveal>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:20 }}>About Performance Lab</div>
          <h2 style={{ fontSize:'clamp(24px, 3vw, 38px)', fontWeight:700, letterSpacing:'-0.03em', margin:'0 0 24px', lineHeight:1.2 }}>What Performance Lab Is For.</h2>
          <p style={{ fontSize:15, color:'#8888a8', lineHeight:1.9, marginBottom:20 }}>
            Performance Lab is the main trading performance product built by ODFTrades. It helps traders
            journal trades, import or record trading activity, review performance, identify behavioural
            patterns, track mistakes, measure execution, analyze results, and turn raw trading data into
            clearer rules for improvement.
          </p>
          <p style={{ fontSize:15, color:'#8888a8', lineHeight:1.9, marginBottom:20 }}>
            It covers the full review workflow: journaling, dashboard analytics, the ODF Score, pattern
            insights, discipline tracking, risk management, calendar views, live charts, broker imports,
            and shareable dashboard analytics — designed for both newer traders looking for structure and
            serious traders looking for measurable growth over time.
          </p>
          <p style={{ fontSize:14, color:'#55556a', lineHeight:1.8 }}>
            Every feature inside Performance Lab exists because a trader needed it — not because it looked
            good on a feature list.
          </p>
        </Reveal>
      </section>

      {/* (Free PDF / Trading Resources removed from Performance Lab — lives on the main ODFTrades site only) */}

      {/* ── FAQ ── */}
      <section id="faq" style={{ padding:'100px 24px', maxWidth:760, margin:'0 auto' }}>
        <Reveal style={{ textAlign:'center', marginBottom:48 }}>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:16 }}>FAQ</div>
          <h2 style={{ fontSize:'clamp(26px, 3.5vw, 42px)', fontWeight:700, letterSpacing:'-0.03em', margin:0 }}>Common questions.</h2>
        </Reveal>
        <div className="landing-faq">
          {FAQS.map((faq, i) => <FAQItem key={i} q={faq.q} a={faq.a} />)}
        </div>
      </section>

      {/* ── HAVE A QUESTION ── */}
      <section id="contact" style={{ padding:'80px 24px', maxWidth:760, margin:'0 auto', textAlign:'center' }}>
        <Reveal>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:16 }}>Get in Touch</div>
          <h2 style={{ fontSize:'clamp(24px, 3vw, 38px)', fontWeight:700, letterSpacing:'-0.03em', margin:'0 0 14px', lineHeight:1.2 }}>Have a question or suggestion?</h2>
          <p style={{ fontSize:14, color:'#8888a8', margin:'0 auto 28px', lineHeight:1.8, maxWidth:480 }}>
            Reach out on Instagram, X, or via email. We read everything.
          </p>
          <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
            <a href="https://instagram.com/odftrades" target="_blank" rel="noopener noreferrer" style={{ display:'inline-flex', alignItems:'center', gap:8, background:'#0d0d14', border:'1px solid #1e1e2e', borderRadius:9, padding:'11px 20px', fontSize:13, color:'#f0f0f5', textDecoration:'none', fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(0,229,160,0.3)';e.currentTarget.style.background='#111118';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='#1e1e2e';e.currentTarget.style.background='#0d0d14';}}>
              <span style={{ display:'inline-flex', alignItems:'center', color:G }}><IconInstagram size={14} /></span> @odftrades
            </a>
            <a href="https://x.com/odftrades" target="_blank" rel="noopener noreferrer" style={{ display:'inline-flex', alignItems:'center', gap:8, background:'#0d0d14', border:'1px solid #1e1e2e', borderRadius:9, padding:'11px 20px', fontSize:13, color:'#f0f0f5', textDecoration:'none', fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(0,229,160,0.3)';e.currentTarget.style.background='#111118';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='#1e1e2e';e.currentTarget.style.background='#0d0d14';}}>
              <span style={{ fontFamily:"'DM Mono',monospace", fontWeight:700 }}>𝕏</span> @odftrades
            </a>
            <button onClick={onContact} style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(0,229,160,0.08)', border:'1px solid rgba(0,229,160,0.25)', borderRadius:9, padding:'11px 20px', fontSize:13, color:G, fontFamily:"'IBM Plex Sans',sans-serif", cursor:'pointer', fontWeight:600, transition:'all 0.2s' }}
              onMouseEnter={e=>{e.currentTarget.style.background='rgba(0,229,160,0.12)';}}
              onMouseLeave={e=>{e.currentTarget.style.background='rgba(0,229,160,0.08)';}}>
              <span style={{ display:'inline-flex', alignItems:'center' }}><IconMail size={14} /></span> Contact Form
            </button>
          </div>
        </Reveal>
      </section>

      {/* ── FINAL CTA ── */}
      <section id="cta" style={{ padding:'100px 24px 80px', textAlign:'center' }}>
        <Reveal>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:24 }}>Get Started</div>
          <div style={{ background:'linear-gradient(135deg, rgba(0,229,160,0.06) 0%, rgba(76,201,240,0.03) 100%)', border:'1px solid rgba(0,229,160,0.12)', borderRadius:20, padding:'60px 40px', maxWidth:700, margin:'0 auto', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:400, height:400, background:'radial-gradient(circle, rgba(0,229,160,0.06) 0%, transparent 70%)', pointerEvents:'none' }} />
            <h2 style={{ fontSize:'clamp(28px, 4vw, 48px)', fontWeight:700, letterSpacing:'-0.03em', margin:'0 0 16px', lineHeight:1.15 }}>
              Track your performance<br />like a professional.
            </h2>
            <p style={{ fontSize:15, color:'#8888a8', marginBottom:32, lineHeight:1.8, maxWidth:440, margin:'0 auto 32px' }}>
              Free for all core features. If you are serious about trading, this is the system you need.
            </p>
            <button
              className="cta-magnetic"
              onClick={onEnter}
              onMouseMove={handleCtaMove}
              style={{ background:G, color:'#0a0a0f', border:'none', borderRadius:10, padding:'16px 48px', fontSize:16, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}
            >
              {t(lang,'startFree')} →
            </button>
          </div>
        </Reveal>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ borderTop:'1px solid #1a1a26', padding:'32px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:16 }}>
        <ODFLogo size="compact" />
        <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
          {[['Privacy', onPrivacy],['Terms', onTerms],['Disclaimer', onDisclaimer],['Contact', onContact]].map(([label, fn]) => (
            <button key={label} onClick={fn} style={{ background:'none', border:'none', color:'#55556a', fontSize:12, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif", transition:'color 0.2s', padding:0 }}
              onMouseEnter={e=>e.target.style.color='#f0f0f5'} onMouseLeave={e=>e.target.style.color='#55556a'}>
              {label}
            </button>
          ))}
        </div>
        <div style={{ fontSize:11, color:'#2a2a3e', fontFamily:"'DM Mono',monospace" }}>
          © {new Date().getFullYear()} ODFTrades Inc. · Educational trading platform & software company
        </div>
      </footer>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @media (max-width: 640px) {
          nav > div:nth-child(1) > div:nth-child(2) { display: none; }
        }
        @media (max-width: 768px) {
          section { padding: 60px 20px !important; }
        }
      `}</style>
    </div>
  );
}
